import type { IsolatedPartnerPointsPerPair } from '@/config/chain/types'
import { CLUSTER_CONFIG, CLUSTER_KEYS } from '@/config/market-config'
import type { NormalizedTokenInfo } from '@/domain/euler'
import type { ReserveStatus } from '@/domain/market-info/reserve-status'
import type { FraxlendPairPauseState } from '@/features/isolated/types/FraxlendPairPauseState'
import { NormalizedUnitNumber, Percentage } from '@/domain/types/NumericValues'
import { Token } from '@/domain/types/Token'
import type { VaultMarket } from '@/features/evault-details/data/products'
import { Address } from 'viem'

export interface ExposureItem {
  collateralAddress: Address
  assetAddress: Address | null
  name: string
  borrowLTV?: number
  liquidationLTV?: number
  allocationUsd?: number
  supplyAPY?: number
}

export interface YieldExposure {
  type: 'vault-collaterals' | 'vault-strategies' | 'isolated-pair' | 'shared-pool'
  collaterals?: ExposureItem[]
  strategies?: ExposureItem[]
  isolatedPair?: {
    collateralToken: Token
    borrowToken: Token
    maxLtv: Percentage
  }
  isSharedPool?: boolean
}

export type YieldSource = 'evault' | 'elend' | 'isolated' | 'pooled'

/**
 * Unified yield opportunity type that aggregates all yield sources
 */
export interface YieldOpportunity {
  /** Unique identifier: `${source}-${chainId}-${address}` */
  id: string
  /** Source type for filtering and routing */
  source: YieldSource
  /** Chain ID where this opportunity exists */
  chainId: number
  /** Contract address (vault, market, or pair address) */
  address: Address
  /** Display name */
  name: string
  /** Token symbol of the yield-bearing token */
  symbol: string
  /** Symbol of the underlying asset (what you deposit) */
  assetSymbol: string
  /** Token info with logo, decimals, etc. */
  tokenInfo?: NormalizedTokenInfo
  /** Underlying token for display */
  token?: Token
  /** Supply/Deposit APY as percentage (e.g., 5.25 for 5.25%) */
  supplyAPY: number
  /** 30-day rolling average supply APY */
  supplyApy30d?: number | null
  /** Total Value Locked in USD */
  tvlUsd: NormalizedUnitNumber | undefined
  /** User's balance of the underlying asset in wallet */
  walletBalance: NormalizedUnitNumber | undefined
  /** For WHYPE vaults: breakdown of native HYPE vs wrapped WHYPE */
  walletBreakdown?: { nativeUsd: number; wrappedUsd: number; nativeAmount: number; wrappedAmount: number }
  /** User's deposited/supplied amount */
  userDeposit: NormalizedUnitNumber | undefined
  /** User's deposited amount in USD */
  userDepositUsd: NormalizedUnitNumber | undefined
  /** Whether user has any position in this opportunity */
  hasPosition: boolean
  /** Curator/Governor name for vaults, or protocol name */
  curatorName?: string
  /** Curator/entity logo URL */
  curatorLogo?: string
  /** Market classification for filtering (Prime, Yield, etc.) */
  market?: VaultMarket
  /** Points programs available (e.g., "hyperliquid", "euler") */
  points?: string[]
  /** Partner points config for this opportunity */
  partnerPoints?: IsolatedPartnerPointsPerPair[]
  /** Exposure data showing collateral relationships. null = data not available (fallback mode), undefined = genuinely no exposure */
  exposure?: YieldExposure | null
  /** Utilization rate (0-1, e.g., 0.85 = 85%) */
  utilization?: number
  /** Aave pooled reserve status when `source === 'pooled'` */
  reserveStatus?: ReserveStatus
  /** Fraxlend pair on-chain pause flags when `source === 'isolated'` */
  fraxlendPauseState?: FraxlendPairPauseState
  /** Additional source-specific data for navigation */
  sourceData: YieldSourceData
}

/**
 * Source-specific data needed for navigation and display
 */
export type YieldSourceData = EVaultSourceData | ELendSourceData | IsolatedSourceData | PooledSourceData

export interface EVaultSourceData {
  type: 'evault'
  /** Total assets in vault */
  totalAssets?: NormalizedUnitNumber
}

export interface ELendSourceData {
  type: 'elend'
  subAccountId?: number
  isCollateralOnly?: boolean
  lendingAPY?: number
  intrinsicAPY?: number
  borrowAPY?: number
  totalBorrowedUsd?: NormalizedUnitNumber
  totalAssets?: NormalizedUnitNumber
  subAccountDeposits?: Array<{ subAccountId: number; depositUsd: number }>
}

export interface IsolatedSourceData {
  type: 'isolated'
  collateralToken: Token
  borrowToken: Token
  utilization: number
  borrowAPY: number
  maxLtv: Percentage
  totalBorrowed?: NormalizedUnitNumber
  totalAssets?: NormalizedUnitNumber
}

export interface PooledSourceData {
  type: 'pooled'
  isCollateralOnly?: boolean
  /** Full APY details with incentives */
  depositAPYDetails?: {
    apy: Percentage | undefined
    incentives: unknown[]
    airdrops: unknown[]
  }
  /** Total borrowed in this market */
  totalBorrowed?: NormalizedUnitNumber
  /** E-mode category */
  eModeCategoryId?: number
  /** Lending APY (from borrower interest) - decompounded from total if intrinsic exists */
  lendingAPY?: number
  /** Intrinsic APY (staking yield, etc.) - shared from elend vaults with same underlying */
  intrinsicAPY?: number
  totalAssets?: NormalizedUnitNumber
}

/**
 * Filter chip label. Derived from `CLUSTER_CONFIG` so every cluster (elend,
 * evault, plus the synthetic `pooled` / `legacy`) contributes a chip, and
 * renaming a cluster propagates here automatically.
 */
export type MarketFilter = string

export type AssetFilter = 'Stablecoins' | 'HYPE'
export type SortField = 'apy' | 'tvl'
export type SortDirection = 'asc' | 'desc'

export const MARKET_FILTERS: MarketFilter[] = CLUSTER_KEYS.map((key) => CLUSTER_CONFIG[key]!.name)
export const ASSET_FILTERS: AssetFilter[] = ['Stablecoins', 'HYPE']
export const SORT_FIELDS: { value: SortField; label: string }[] = [
  { value: 'apy', label: 'APY' },
  { value: 'tvl', label: 'TVL' },
]

export interface UnifiedEarnFilter {
  markets: MarketFilter[]
  assets: AssetFilter[]
  assetFilter: string[]
  inWalletOnly: boolean
  depositedOnly: boolean
  searchQuery: string
  sortBy: SortField
  sortDirection: SortDirection
}
